import React, { FC, useRef } from "react";
import {
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  useTheme,
} from "@mui/material";
import { LinkItem } from "./LinkItem";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { ItemVariant } from "./ItemVariant";
import { Layout } from "./Layout";
import { LayoutDirection } from "./LayoutDirection";
import { useClassName } from "./useResolveClassName";
import { useSxMerge } from "shared-ts-mui";
import { useResolveItemName } from "./useResolveItemName";
import { useResolveItemColors } from "./useResolveItemColors";

export interface LinkListItemProps {
  item: LinkItem;
  variant?: ItemVariant;
  layout: Layout;
  layoutDirection?: LayoutDirection;
  hideLabel?: boolean;
  sx?: SxProps;
}

export const LinkListItem: FC<LinkListItemProps> = (props) => {
  const { item } = props;
  const target = item.target ?? "_blank";
  const ref = useRef<HTMLLIElement>(null);
  const name = useResolveItemName(item, ref.current);
  const hideLabel = props.hideLabel ?? false;
  const displayLabel = !hideLabel;
  const classNameRoot = useClassName(props, ["Item", "LinkItem"]);
  const classNameLink = useClassName(props, ["LinkItem-link"]);
  const {
    onColor,
    onContainerColorWhenSelected,
    containerColorWhenSelected,
    containerColorOnHover,
    containerColorWhenSelectedOnHover,
  } = useResolveItemColors();
  const theme = useTheme();
  const sx = useSxMerge(props.sx, {
    justifyContent: hideLabel ? "center" : undefined,
    "&.direction-column": {
      "&.layout-medium": {
        borderRadius: "9px",
      },
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  });
  return (
    <ListItem ref={ref} className={classNameRoot} sx={sx}>
      <Link
        className={classNameLink}
        target={target}
        href={item.href}
        underline={"none"}
        sx={{
          color: "text.primary",
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          borderRadius: "inherit",

          ">.MuiListItemText-root": {
            ">.MuiTypography-root": {
              fontSize: "inherit",
              textAlign: "inherit",
            },
          },

          "&.layout-medium": {
            fontSize: "0.75rem",
            "&.direction-column": {
              ".MuiListItemIcon-root": {
                height: "32px",
                width: "56px",
              },
            },
          },

          "&.layout-compact": {
            fontSize: "0.75rem",
            "&.direction-column": {
              ".MuiListItemIcon-root": {
                height: "32px",
                width: "56px",
              },
            },
          },

          "&.direction-row": {
            ".Link-icon": {
              marginLeft: "auto",
              minWidth: "unset",
            },
          },

          "&.direction-column": {
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            gap: "4px",
            ".MuiListItemIcon-root": {
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
            },
            "&:hover": {
              backgroundColor: "unset",
              ".MuiListItemIcon-root:not(.Link-icon)": {
                backgroundColor: containerColorOnHover,
              },
            },
          },

          ".MuiListItemIcon-root:first-child": {
            minWidth: hideLabel ? "unset" : "36px",
          },
        }}
      >
        {item.icon != null && <ListItemIcon>{item.icon}</ListItemIcon>}
        {displayLabel && <ListItemText primary={name} sx={{ mt: 0, mb: 0 }} />}
        {displayLabel && (
          <ListItemIcon className={"Link-icon"}>
            <OpenInNewRoundedIcon fontSize="small" />
          </ListItemIcon>
        )}
      </Link>
    </ListItem>
  );
};
